import { useState, useEffect } from 'react';
import { Vote, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import pollsImage from '@/assets/empty-state-polls.jpg';
import { useSocialProof } from '@/hooks/useSocialProof';

interface PollsEmptyStateProps {
  onCreatePoll?: () => void;
}

export function PollsEmptyState({ onCreatePoll }: PollsEmptyStateProps) {
  const [displayText, setDisplayText] = useState('');
  const fullText = 'Create a Poll';
  const { totalPolls } = useSocialProof();

  // Typewriter effect for button text
  useEffect(() => {
    let currentIndex = 0;
    const typeInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setDisplayText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typeInterval);
      }
    }, 80);

    return () => clearInterval(typeInterval);
  }, []);

  return (
    <div className="fixed inset-0 z-0 overflow-hidden">
      {/* Full-bleed image background */}
      <motion.img
        src={pollsImage}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        animate={{ scale: [1.1, 1.18, 1.1], y: ['0%', '-1.5%', '0%'] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      />
      
      {/* Gradient overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
      
      {/* Content positioned at bottom */}
      <div className="absolute inset-x-0 bottom-0 px-6 flex flex-col items-center" style={{ paddingBottom: 'calc(7rem + env(safe-area-inset-bottom))' }}>
        {/* Community indicator */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-1.5 text-white/90 text-sm font-medium mb-4"
        >
          <Users className="w-4 h-4" />
          <span>{totalPolls > 0 ? `Join ${totalPolls.toLocaleString()}+ polls` : 'Get opinions from the community'}</span>
        </motion.div>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="text-2xl font-bold text-white mb-2 drop-shadow-lg text-center"
        >
          Can't decide? Ask the crowd
        </motion.h2>
        
        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-white/80 mb-6 drop-shadow text-center max-w-xs"
        >
          Create polls and let others vote on your fashion choices
        </motion.p>

        {/* Poll type hints */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="flex gap-3 mb-6"
        >
          {['This or That', 'Pick One', 'Yes or No'].map((type, index) => (
            <motion.div
              key={type}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + index * 0.05 }}
              className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium border border-white/20"
            >
              {type}
            </motion.div>
          ))}
        </motion.div>

        {/* Main CTA with typewriter */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35, type: "spring", stiffness: 300, damping: 25 }}
          className="relative"
        >
          {/* Pulsing glow ring */}
          <motion.div
            className="absolute inset-0 rounded-full bg-white/30"
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.6, 0, 0.6],
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.button
            onClick={onCreatePoll}
            className="relative flex items-center gap-3 px-8 py-4 rounded-full bg-white text-black font-semibold text-lg shadow-2xl active:scale-95 transition-transform"
            whileTap={{ scale: 0.95 }}
          >
            <Vote className="w-6 h-6" />
            <span className="min-w-[120px]">
              {displayText}
              <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                className="inline-block w-0.5 h-5 bg-black ml-0.5 align-middle"
              />
            </span>
          </motion.button>
        </motion.div>

        {/* Hint */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-sm text-white/60 mt-5 drop-shadow text-center"
        >
          Upload photos and get instant feedback ✨
        </motion.p>
      </div>
    </div>
  );
}
